import { Box, Button, CircularProgress } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useRef, useState } from 'react'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { MachineEvent, TextEventProps } from '../../../ts/MachineEvent';
import { Cargo } from '../../../ts/Cargo';

export type TimelineItem = MachineEvent | Cargo | TextEventProps;

interface ScanHistoryListProps {
    data: TimelineItem[];
    itemContent: (index: number, item: TimelineItem) => React.ReactNode;
    onEndReached?: () => void;
    onStartReached?: () => void;
    onAtTopChange?: (atTop: boolean) => void;
    loadingOlder?: boolean;
    listHeight?: string | number;
    refreshEnabled?: boolean;
}

// Shared virtualized scan list + scroll-to-top Refresh overlay. The desktop and mobile
// history views differ only in how each row renders, so they pass their own itemContent.
function ScanHistoryList(props: ScanHistoryListProps) {
    const { data, itemContent, onEndReached, onStartReached, onAtTopChange,
        loadingOlder, listHeight = '80vh', refreshEnabled = false } = props;
    const { t } = useTranslation();
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const [atTop, setAtTop] = useState(true);

    const handleAtTopStateChange = (value: boolean) => {
        setAtTop(value);
        onAtTopChange?.(value);
    };

    // Scroll the scroller fully to the top (not scrollToIndex, which stops below the 8px Header) so
    // Virtuoso reports at-top; also flag at-top immediately so the poll resumes without extra scrolling.
    const scrollToTop = () => {
        virtuosoRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        handleAtTopStateChange(true);
    };

    return (
        <Box sx={{ position: 'relative', height: listHeight }}>
            {refreshEnabled && !atTop && (
                <Button
                    onClick={scrollToTop}
                    variant="contained"
                    size="small"
                    startIcon={<RefreshIcon />}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 20,
                        borderRadius: 999,
                        textTransform: 'none',
                        lineHeight: 1,
                        boxShadow: 3,
                    }}
                >
                    {t('control.eventHistory.refresh')}
                </Button>
            )}
            <Virtuoso
                ref={virtuosoRef}
                style={{ height: '100%' }}
                data={data}
                computeItemKey={(index, item) => item instanceof Cargo ? `cargo-${item.cargoFolder || item.iso8601 || index}` : `event-${index}`}
                itemContent={itemContent}
                endReached={onEndReached}
                startReached={onStartReached}
                atTopStateChange={handleAtTopStateChange}
                components={{
                    // Replaces the top padding the old MUI List provided, absorbing the first card's negative margin.
                    Header: () => <Box sx={{ height: 8 }} />,
                    Footer: () => loadingOlder
                        ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                        : null
                }}
            />
        </Box>
    )
}

export default React.memo(ScanHistoryList);
